// src/components/editor/EditorToolbar.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Menu,
  Typography,
  TextField,
  Divider,
  Tooltip,
} from '@mui/material';

import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import FormatStrikethrough from '@mui/icons-material/StrikethroughS';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRight from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustify from '@mui/icons-material/FormatAlignJustify';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import FormatIndentIncrease from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecrease from '@mui/icons-material/FormatIndentDecrease';
import FormatLineSpacing from '@mui/icons-material/FormatLineSpacing';
import FormatColorText from '@mui/icons-material/FormatColorText';
import FormatColorFill from '@mui/icons-material/FormatColorFill';
import FormatClear from '@mui/icons-material/FormatClear';
import Subscript from '@mui/icons-material/Subscript';
import Superscript from '@mui/icons-material/Superscript';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import HeaderIcon from '@mui/icons-material/VerticalAlignTop';
import FooterIcon from '@mui/icons-material/VerticalAlignBottom';
import '../../styles/ribbon.css';
import { FONT_FAMILIES, FONT_SIZES, TEXT_STYLES } from '../../config/fonts';
import { BOOK_SIZES, dimensionsLabel, pageSizePx } from '../../config/bookSizes';


// Page Setup offers book trim sizes, not office paper.
//
// It used to list Letter / A4 / Legal / Tabloid — none of which is a book, and
// the author had already chosen a real size at the quote and had a cover drawn at
// it. Sizes come from `src/config/bookSizes.js`, the one table the quote, the
// cover designer and the PDF all read, so the manuscript can no longer be a
// different size from the book it becomes.

const COLORS = [
  '#000000','#FF0000','#00CC00','#0000FF','#FFFF00','#FF00FF',
  '#00FFFF','#FFFFFF','#808080','#FFA500','#800080','#008080',
];

// Exported so the inline format bar offers the same swatches as this panel —
// two palettes drifting apart would mean the same document got highlighted in
// near-but-not-quite matching colours depending on which control was used.
export const HIGHLIGHT_COLORS = [
  '#FFFF00','#00FF00','#FF69B4','#00BFFF','#FFA500',
  '#FF6347','#98FF98','#E0E0E0','none',
];

// ── shared button style ─────────────────────────────────────────────────────
const iconBtn = (active = false) => ({
  backgroundColor: active ? '#F5D76E' : '#5a4d99',
  color: active ? '#5a4d99' : '#F5D76E',
  width: 32, height: 32, minWidth: 32, flexShrink: 0,
  transition: 'all 0.2s ease',
  boxShadow: active ? '0 0 8px rgba(245,215,110,0.5)' : 'none',
  '&:hover': {
    backgroundColor: active ? '#e6c757' : '#6a5da9',
    boxShadow: '0 0 10px rgba(245,215,110,0.6)',
  },
});

const sectionLabel = (text) => (
  <Typography sx={{ color: '#F5D76E', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.5, mt: 1 }}>
    {text}
  </Typography>
);

export default function EditorToolbar({
  onBookCoverClick,
  onBold, onItalic, onUnderline, onStrikethrough,
  onSubscript, onSuperscript, onClearFormatting,
  onAlignLeft, onAlignCenter, onAlignRight, onAlignJustify,
  onBulletedList, onNumberedList, onIndentIncrease, onIndentDecrease,
  onUndo, onRedo,
  onFontFamilyChange, onFontSizeChange,
  onLineSpacingChange, onTextColorChange, onHighlightColorChange,
  onParagraphSpacingChange,
  onToggleHeader, onToggleFooter,
  onPageSetupChange,
  trimSize,
  onTrimSizeChange,
  onCaptureSelection,
  onApplyHeading,
  currentFormatting,
}) {
  const [fontFamily, setFontFamily]   = useState('Arial'); // stores display name
  const [fontSize, setFontSize]       = useState(currentFormatting?.fontSize   || '14px');
  const [textStyle, setTextStyle]     = useState('normal');
  const [lineSpacing, setLineSpacing] = useState('1.5');
  const [fontSearch, setFontSearch]   = useState('');
  const [customSize, setCustomSize]   = useState('');
  const [textColor, setTextColor]     = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('none');
  const [paraSpacingBefore, setParaSpacingBefore] = useState('0');
  const [paraSpacingAfter,  setParaSpacingAfter]  = useState('0');
  const [orientation, setOrientation] = useState('portrait');

  const [isBoldActive,      setIsBoldActive]      = useState(false);
  const [isItalicActive,    setIsItalicActive]    = useState(false);
  const [isUnderlineActive, setIsUnderlineActive] = useState(false);

  // menus
  const [lineSpacingAnchorEl, setLineSpacingAnchorEl] = useState(null);
  const [colorAnchorEl,       setColorAnchorEl]       = useState(null);
  const [highlightAnchorEl,   setHighlightAnchorEl]   = useState(null);

  const normalizeFontSize = (size) => {
    if (!size) return 14;
    const n = parseInt(size);
    return isNaN(n) ? 14 : n;
  };

  React.useEffect(() => {
    if (!currentFormatting) return;
    // currentFormatting.fontFamily is the first part of whatever stack is in the DOM
    // (e.g. "EB Garamond" from '"EB Garamond", Garamond, ...')
    const rawFamily = currentFormatting.fontFamily || 'Arial';
    const matchedFont = FONT_FAMILIES.find(
      f => f.name.toLowerCase() === rawFamily.toLowerCase()
    );
    setFontFamily(matchedFont?.name ?? rawFamily);
    const newSize   = currentFormatting.fontSize   || '16px';
    setFontSize(newSize);
    setIsBoldActive(currentFormatting.bold      || false);
    setIsItalicActive(currentFormatting.italic  || false);
    setIsUnderlineActive(currentFormatting.underline || false);

    let matched = 'normal';
    if (currentFormatting.textStyle) {
      matched = currentFormatting.textStyle;
    } else {
      const sz  = normalizeFontSize(newSize);
      const b   = currentFormatting.bold;
      const it  = currentFormatting.italic;
      const al  = currentFormatting.textAlign || 'left';
      if      (sz >= 26 && sz <= 28 && b  && al === 'center') matched = 'title';
      else if (sz >= 17 && sz <= 19 && it && al === 'center') matched = 'subtitle';
      else if (sz >= 23 && sz <= 25 && b)                      matched = 'heading1';
      else if (sz >= 19 && sz <= 21 && b)                      matched = 'heading2';
      else if (sz >= 17 && sz <= 19 && b)                      matched = 'heading3';
      else if (sz >= 15 && sz <= 17 && b)                      matched = 'heading4';
      else if (it && !b && sz >= 13 && sz <= 15)               matched = 'quote';
    }
    setTextStyle(matched);
  }, [currentFormatting]);

  const handleFontFamily = (name) => {
    setFontFamily(name);
    const entry = FONT_FAMILIES.find(f => f.name === name);
    // pass the full CSS stack so iOS/Windows fallbacks apply
    onFontFamilyChange?.(entry?.stack ?? name);
  };
  const handleFontSize   = (value) => { setFontSize(value);   onFontSizeChange?.(value); };

  const handleTextStyle = (value) => {
    setTextStyle(value);
    const style = TEXT_STYLES.find(s => s.value === value);
    if (!style) return;
    // Stamp the block-level heading marker the navigation outline reads. Title
    // and Heading 1 map to level 1; Heading 2-4 to their number; everything else
    // (Normal/Subtitle/Quote) clears the marker (level 0).
    const headingLevel =
      value === 'title' || value === 'heading1' ? 1 :
      value === 'heading2' ? 2 :
      value === 'heading3' ? 3 :
      value === 'heading4' ? 4 : 0;
    onApplyHeading?.(headingLevel);
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (!range.collapsed) {
        const span = document.createElement('span');
        span.setAttribute('data-text-style', value);
        try { range.surroundContents(span); }
        catch { const f = range.extractContents(); span.appendChild(f); range.insertNode(span); }
      }
    }
    setTimeout(() => { if (style.font)  handleFontFamily(style.font); },   0);
    setTimeout(() => { if (style.size)  handleFontSize(style.size); },    50);
    setTimeout(() => {
      if      (style.align === 'left')   onAlignLeft?.();
      else if (style.align === 'center') onAlignCenter?.();
      else if (style.align === 'right')  onAlignRight?.();
    }, 100);
    setTimeout(() => { if (style.bold)   onBold?.(); },   150);
    setTimeout(() => { if (style.italic) onItalic?.(); }, 200);
  };

  const handleLineSpacing = (value) => {
    setLineSpacing(value);
    onLineSpacingChange?.(value);
    setLineSpacingAnchorEl(null);
  };

  const handleTextColor = (color) => {
    setTextColor(color);
    onTextColorChange?.(color);
    setColorAnchorEl(null);
  };

  const handleHighlightColor = (color) => {
    setHighlightColor(color);
    onHighlightColorChange?.(color === 'none' ? 'transparent' : color);
    setHighlightAnchorEl(null);
  };

  const handleCustomSize = () => {
    if (customSize && /^\d+$/.test(customSize)) {
      handleFontSize(`${customSize}px`);
      setCustomSize('');
    }
  };

  // Trim sizes are defined portrait; landscape simply swaps width and height.
  // Orientation is local state so that changing one does not reset the other —
  // picking a new size used to silently put the page back to portrait.
  const applyPaper = (sizeId, orient) => {
    const { width, height } = pageSizePx(sizeId);
    onPageSetupChange?.(orient === 'landscape'
      ? { width: `${height}px`, height: `${width}px` }
      : { width: `${width}px`,  height: `${height}px` });
  };

  // The size belongs to the draft, not to this toolbar: it decides what the PDF
  // prints at and what size cover the book needs. So the choice is handed up, and
  // the page only re-flows once the change has actually been taken.
  const handleTrimSize = (sizeId) => onTrimSizeChange?.(sizeId);

  const handleOrientation = (orient) => {
    setOrientation(orient);
    applyPaper(trimSize, orient);
  };

  // Re-flow when the draft's size changes — on load, and after the author
  // confirms a change.
  useEffect(() => {
    if (trimSize) applyPaper(trimSize, orientation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimSize]);

  const filteredFonts = FONT_FAMILIES.filter(f =>
    f.name.toLowerCase().includes(fontSearch.toLowerCase())
  );

  // ── shared select sx ──────────────────────────────────────────────────────
  const selectSx = {
    backgroundColor: '#D4AF37', color: '#000', fontSize: '11px', height: '28px',
    '& .MuiSelect-select': { py: 0.5, pr: '20px !important' },
    '& .MuiSelect-icon': { color: '#000', right: '2px' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  };
  const menuPaperSx = {
    maxHeight: 300, bgcolor: '#5a4d99',
    '& .MuiMenuItem-root': {
      fontSize: '11px', color: '#F5D76E',
      '&:hover': { bgcolor: '#4A3F8C' },
    },
  };
  const searchFieldSx = {
    '& .MuiInputBase-root': { fontSize: '11px', height: '24px', bgcolor: '#5a4d99', color: '#F5D76E' },
    '& .MuiInputBase-input': { py: 0.5, '&::placeholder': { color: '#F5D76E', opacity: 0.6 } },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#F5D76E' },
  };

  return (
    <Box className="format-toolbar-vertical">

      {/* ── SECTION: Text Style ─────────────────────────────────────── */}
      {sectionLabel('Style')}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, width: '100%' }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={textStyle}
            onChange={(e) => handleTextStyle(e.target.value)}
            displayEmpty
            sx={selectSx}
          >
            {TEXT_STYLES.map((s) => (
              <MenuItem key={s.value} value={s.value} sx={{ fontSize: '11px' }}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── SECTION: Font ───────────────────────────────────────────── */}
      {sectionLabel('Font')}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, width: '100%' }}>
        <FormControl size="small" sx={{ flex: 2 }}>
          <Select
            value={fontFamily}
            onChange={(e) => handleFontFamily(e.target.value)}
            onMouseDown={() => onCaptureSelection?.()}
            onTouchStart={() => onCaptureSelection?.()}
            displayEmpty
            onOpen={() => setFontSearch('')}
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
            sx={selectSx}
            renderValue={(name) => {
              const entry = FONT_FAMILIES.find(f => f.name === name);
              return (
                <span style={{ fontFamily: entry?.stack ?? name, fontSize: '11px' }}>{name}</span>
              );
            }}
          >
            <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, bgcolor: '#4A3F8C', zIndex: 1 }}>
              <TextField
                size="small" placeholder="Search fonts..."
                value={fontSearch}
                onChange={(e) => setFontSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={searchFieldSx}
              />
            </Box>
            {filteredFonts.map((ff) => (
              <MenuItem key={ff.name} value={ff.name} sx={{ fontSize: '11px' }}>
                {/* inline style bypasses MUI theme so the font actually renders */}
                <span style={{ fontFamily: ff.stack }}>{ff.name}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={fontSize}
            onChange={(e) => handleFontSize(e.target.value)}
            onMouseDown={() => onCaptureSelection?.()}
            onTouchStart={() => onCaptureSelection?.()}
            displayEmpty
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
            sx={selectSx}
          >
            <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, bgcolor: '#4A3F8C', zIndex: 1 }}>
              <TextField
                size="small" placeholder="Custom size..."
                value={customSize}
                onChange={(e) => setCustomSize(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleCustomSize(); }}
                onClick={(e) => e.stopPropagation()}
                sx={searchFieldSx}
              />
            </Box>
            {FONT_SIZES.map((sz) => (
              <MenuItem key={sz} value={sz} sx={{ fontSize: '11px' }}>{sz}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── SECTION: Basic formatting ───────────────────────────────── */}
      {sectionLabel('Format')}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Tooltip title="Bold"><IconButton size="small" onClick={() => onBold?.()}          sx={iconBtn(isBoldActive)}><FormatBold sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Italic"><IconButton size="small" onClick={() => onItalic?.()}        sx={iconBtn(isItalicActive)}><FormatItalic sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Underline"><IconButton size="small" onClick={() => onUnderline?.()}     sx={iconBtn(isUnderlineActive)}><FormatUnderlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Strikethrough"><IconButton size="small" onClick={() => onStrikethrough?.()} sx={iconBtn()}><FormatStrikethrough sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Subscript"><IconButton size="small" onClick={() => onSubscript?.()}    sx={iconBtn()}><Subscript sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Superscript"><IconButton size="small" onClick={() => onSuperscript?.()}  sx={iconBtn()}><Superscript sx={{ fontSize: 16 }} /></IconButton></Tooltip>

        {/* Text color */}
        <Tooltip title="Text Color">
          <IconButton size="small" onClick={(e) => setColorAnchorEl(e.currentTarget)} sx={iconBtn()}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
              <FormatColorText sx={{ fontSize: 14, color: '#F5D76E' }} />
              <Box sx={{ width: 14, height: 3, bgcolor: textColor, borderRadius: '1px', border: textColor === '#FFFFFF' ? '1px solid #888' : 'none' }} />
            </Box>
          </IconButton>
        </Tooltip>

        {/* Highlight color */}
        <Tooltip title="Highlight Color">
          <IconButton size="small" onClick={(e) => setHighlightAnchorEl(e.currentTarget)} sx={{ ...iconBtn(), backgroundColor: highlightColor === 'none' ? '#5a4d99' : highlightColor }}>
            <FormatColorFill sx={{ fontSize: 16, color: '#F5D76E' }} />
          </IconButton>
        </Tooltip>

        {/* Clear formatting */}
        <Tooltip title="Clear Formatting">
          <IconButton size="small" onClick={() => onClearFormatting?.()} sx={iconBtn()}>
            <FormatClear sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Text Color Menu */}
      <Menu anchorEl={colorAnchorEl} open={Boolean(colorAnchorEl)} onClose={() => setColorAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: '#5a4d99', p: 1.5 } }}>
        <Typography sx={{ color: '#F5D76E', fontSize: '11px', mb: 0.5 }}>Text Color</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
          {COLORS.map(c => (
            <Box key={c} onClick={() => handleTextColor(c)} sx={{ width: 28, height: 28, bgcolor: c, border: textColor === c ? '2px solid #F5D76E' : '1px solid #ccc', cursor: 'pointer', borderRadius: '4px', '&:hover': { opacity: 0.8 } }} />
          ))}
        </Box>
      </Menu>

      {/* Highlight Color Menu */}
      <Menu anchorEl={highlightAnchorEl} open={Boolean(highlightAnchorEl)} onClose={() => setHighlightAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: '#5a4d99', p: 1.5 } }}>
        <Typography sx={{ color: '#F5D76E', fontSize: '11px', mb: 0.5 }}>Highlight Color</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
          {HIGHLIGHT_COLORS.map(c => (
            <Box key={c} onClick={() => handleHighlightColor(c)}
              sx={{ width: 28, height: 28, bgcolor: c === 'none' ? 'transparent' : c, border: highlightColor === c ? '2px solid #F5D76E' : '1px solid #ccc', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { opacity: 0.8 } }}>
              {c === 'none' && <Typography sx={{ fontSize: '8px', color: '#F5D76E' }}>none</Typography>}
            </Box>
          ))}
        </Box>
      </Menu>

      <Divider sx={{ borderColor: '#6a5da9', my: 1 }} />

      {/* ── SECTION: Alignment ──────────────────────────────────────── */}
      {sectionLabel('Alignment')}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Tooltip title="Align Left">   <IconButton size="small" onClick={() => onAlignLeft?.()}    sx={iconBtn()}><FormatAlignLeft    sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Align Center"> <IconButton size="small" onClick={() => onAlignCenter?.()}  sx={iconBtn()}><FormatAlignCenter  sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Align Right">  <IconButton size="small" onClick={() => onAlignRight?.()}   sx={iconBtn()}><FormatAlignRight   sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Justify">      <IconButton size="small" onClick={() => onAlignJustify?.()} sx={iconBtn()}><FormatAlignJustify sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Line Spacing">
          <IconButton size="small" onClick={(e) => setLineSpacingAnchorEl(e.currentTarget)} sx={iconBtn()}>
            <FormatLineSpacing sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Line Spacing Menu */}
      <Menu anchorEl={lineSpacingAnchorEl} open={Boolean(lineSpacingAnchorEl)} onClose={() => setLineSpacingAnchorEl(null)}
        PaperProps={{ sx: { bgcolor: '#5a4d99', '& .MuiMenuItem-root': { color: '#F5D76E', fontSize: '11px', '&:hover': { bgcolor: '#4A3F8C' }, '&.Mui-selected': { bgcolor: '#4A3F8C' } } } }}>
        {[['1.0', 'Single (1.0)'], ['1.15', '1.15'], ['1.5', '1.5'], ['2.0', 'Double (2.0)'], ['2.5', '2.5'], ['3.0', '3.0']].map(([v, label]) => (
          <MenuItem key={v} onClick={() => handleLineSpacing(v)} selected={lineSpacing === v}>{label}</MenuItem>
        ))}
      </Menu>

      {/* Paragraph Spacing */}
      {sectionLabel('Paragraph Spacing')}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <Typography sx={{ color: '#F5D76E', fontSize: '10px', minWidth: 36 }}>Before</Typography>
        <TextField
          size="small" value={paraSpacingBefore} inputProps={{ style: { textAlign: 'center', padding: '2px 4px', fontSize: '11px', color: '#F5D76E' } }}
          onChange={(e) => setParaSpacingBefore(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => onParagraphSpacingChange?.({ before: Number(paraSpacingBefore) })}
          onKeyDown={(e) => { if (e.key === 'Enter') onParagraphSpacingChange?.({ before: Number(paraSpacingBefore) }); }}
          sx={{ width: 48, '& .MuiOutlinedInput-root': { bgcolor: '#4A3F8C', '& fieldset': { borderColor: '#6a5da9' } } }}
        />
        <Typography sx={{ color: '#F5D76E', fontSize: '10px', minWidth: 24 }}>px</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <Typography sx={{ color: '#F5D76E', fontSize: '10px', minWidth: 36 }}>After</Typography>
        <TextField
          size="small" value={paraSpacingAfter} inputProps={{ style: { textAlign: 'center', padding: '2px 4px', fontSize: '11px', color: '#F5D76E' } }}
          onChange={(e) => setParaSpacingAfter(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => onParagraphSpacingChange?.({ after: Number(paraSpacingAfter) })}
          onKeyDown={(e) => { if (e.key === 'Enter') onParagraphSpacingChange?.({ after: Number(paraSpacingAfter) }); }}
          sx={{ width: 48, '& .MuiOutlinedInput-root': { bgcolor: '#4A3F8C', '& fieldset': { borderColor: '#6a5da9' } } }}
        />
        <Typography sx={{ color: '#F5D76E', fontSize: '10px', minWidth: 24 }}>px</Typography>
      </Box>

      <Divider sx={{ borderColor: '#6a5da9', my: 1 }} />

      {/* ── SECTION: Lists ──────────────────────────────────────────── */}
      {sectionLabel('Lists')}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Bulleted List">   <IconButton size="small" onClick={() => onBulletedList?.()}  sx={iconBtn()}><FormatListBulleted   sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Numbered List">   <IconButton size="small" onClick={() => onNumberedList?.()}  sx={iconBtn()}><FormatListNumbered   sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Increase Indent"> <IconButton size="small" onClick={() => onIndentIncrease?.()} sx={iconBtn()}><FormatIndentIncrease sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Decrease Indent"> <IconButton size="small" onClick={() => onIndentDecrease?.()} sx={iconBtn()}><FormatIndentDecrease sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Box>

      <Divider sx={{ borderColor: '#6a5da9', my: 1 }} />

      {/* ── SECTION: Headers & Footers ──────────────────────────────── */}
      {sectionLabel('Headers & Footers')}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Tooltip title="Toggle Header">
          <Button size="small" onClick={() => onToggleHeader?.()}
            startIcon={<HeaderIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '10px', color: '#F5D76E', bgcolor: '#5a4d99', textTransform: 'none', flex: 1, py: 0.5, '&:hover': { bgcolor: '#6a5da9' } }}>
            Header
          </Button>
        </Tooltip>
        <Tooltip title="Toggle Footer">
          <Button size="small" onClick={() => onToggleFooter?.()}
            startIcon={<FooterIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '10px', color: '#F5D76E', bgcolor: '#5a4d99', textTransform: 'none', flex: 1, py: 0.5, '&:hover': { bgcolor: '#6a5da9' } }}>
            Footer
          </Button>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: '#6a5da9', my: 1 }} />

      {/* ── SECTION: Page Setup ─────────────────────────────────────── */}
      {sectionLabel('Page Setup')}

      {/* Paper size */}
      <FormControl size="small" sx={{ width: '100%', mb: 1 }}>
        <Select
          value={trimSize || ''}
          onChange={(e) => handleTrimSize(e.target.value)}
          MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          sx={selectSx}
        >
          {BOOK_SIZES.map((size) => (
            <MenuItem key={size.id} value={size.id} sx={{ fontSize: '11px' }}>
              {size.label} ({dimensionsLabel(size.id)})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Orientation — composes with the selected paper size (see applyPaper) */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        {['portrait', 'landscape'].map((orient) => {
          const active = orientation === orient;
          return (
            <Button
              key={orient}
              size="small"
              onClick={() => handleOrientation(orient)}
              sx={{
                fontSize: '10px', textTransform: 'capitalize', flex: 1, py: 0.5,
                color: active ? '#1A1A1A' : '#F5D76E',
                bgcolor: active ? '#D4AF37' : '#5a4d99',
                fontWeight: active ? 700 : 400,
                '&:hover': { bgcolor: active ? '#F5D76E' : '#6a5da9' },
              }}
            >
              {orient}
            </Button>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: '#6a5da9', my: 1 }} />

      {/* ── Undo / Redo ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 1 }}>
        <Tooltip title="Undo"><IconButton size="small" onClick={() => onUndo?.()} sx={iconBtn()}><Undo sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Redo"><IconButton size="small" onClick={() => onRedo?.()} sx={iconBtn()}><Redo sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Box>

    </Box>
  );
}
